import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { useLab } from '../LabContext';
import { LabLayout, Card } from '../LabUI';

// Same resolver as LabContext for /api/lab/auth.
const authBase = () => {
  const path = '/api/lab/auth';
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return `${configured || ''}${path}`;
  if (!configured) return path;
  try {
    const u = new URL(configured);
    if (u.origin === window.location.origin) return `${configured}${path}`;
  } catch (_e) {
    /* same-origin fall-through */
  }
  return path;
};

const ParentChangePassword = () => {
  const { language, user } = useLab();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next1, setNext1] = useState('');
  const [next2, setNext2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (next1.length < 8) {
      setError(
        language === 'ru'
          ? 'Новый пароль должен быть не короче 8 символов.'
          : 'New password must be at least 8 characters.'
      );
      return;
    }
    if (next1 !== next2) {
      setError(language === 'ru' ? 'Пароли не совпадают.' : 'Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${authBase()}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword: current, newPassword: next1 })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = j.detail;
        throw new Error(
          Array.isArray(detail) ? detail.map((d) => d.msg).join('; ') : detail || 'Failed'
        );
      }
      setSuccess(
        language === 'ru'
          ? 'Пароль обновлён. Старый пароль больше не работает.'
          : 'Password updated. The old password no longer works.'
      );
      setCurrent('');
      setNext1('');
      setNext2('');
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <LabLayout>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/lab/parent/users')}
          className="inline-flex items-center gap-1.5 text-amber-200/70 text-sm font-sans hover:text-amber-200"
          data-testid="parent-account-back"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'ru' ? 'К пользователям' : 'Back to users'}
        </button>
      </div>

      <section className="mt-3">
        <h1
          className="text-3xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="parent-account-title"
        >
          {language === 'ru' ? 'Мой пароль' : 'My password'}
        </h1>
        <p className="mt-1 text-amber-200/65 font-serif text-sm">
          {language === 'ru'
            ? `Аккаунт «${user?.login}» (${user?.role}).`
            : `Account "${user?.login}" (${user?.role}).`}
        </p>
      </section>

      <Card className="mt-5 p-4">
        <form onSubmit={submit} className="space-y-3" data-testid="parent-account-form">
          <Field
            label={language === 'ru' ? 'Текущий пароль' : 'Current password'}
            value={current}
            onChange={setCurrent}
            type="password"
            testid="parent-account-current"
          />
          <Field
            label={language === 'ru' ? 'Новый пароль' : 'New password'}
            value={next1}
            onChange={setNext1}
            type="password"
            testid="parent-account-new"
          />
          <Field
            label={language === 'ru' ? 'Повтор нового пароля' : 'Repeat new password'}
            value={next2}
            onChange={setNext2}
            type="password"
            testid="parent-account-new2"
          />

          {error && (
            <div
              className="text-red-300/90 text-sm font-sans"
              data-testid="parent-account-error"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              className="text-emerald-300/90 text-sm font-sans"
              data-testid="parent-account-success"
            >
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !current || !next1}
            data-testid="parent-account-submit"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-40 disabled:cursor-not-allowed text-[#1a1208] font-sans transition-colors"
          >
            <KeyRound className="w-4 h-4" />
            {busy
              ? language === 'ru'
                ? 'Сохраняем…'
                : 'Saving…'
              : language === 'ru'
                ? 'Сохранить новый пароль'
                : 'Save new password'}
          </button>
        </form>
      </Card>

      <div className="mt-6 text-xs text-amber-200/45 font-sans">
        <Link to="/lab/parent" data-testid="parent-account-back-to-dash">
          {language === 'ru' ? '← Кабинет родителя' : '← Parent dashboard'}
        </Link>
      </div>
    </LabLayout>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-amber-200/60 font-sans">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      autoComplete="off"
      className="mt-1 w-full px-3 py-2 rounded-xl bg-[#0c1230]/60 border border-amber-300/15 text-[#f5ebe0] placeholder:text-amber-200/30 focus:outline-none focus:border-amber-300/40 font-sans text-base"
    />
  </label>
);

export default ParentChangePassword;
